import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useDarkMode } from '../../../context/DarkModeContext';
import { RubricCoverageMetric } from '../../../utils/reviewPrepCalculations';
import { 
  CheckCircleIcon, 
  ExclamationTriangleIcon, 
  XCircleIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

interface EvidenceHealthCheckProps {
  coverage: RubricCoverageMetric[];
  onQuickAdd: (rubricItemId: string, category: string, description: string) => void;
}

const EvidenceHealthCheck: React.FC<EvidenceHealthCheckProps> = ({ coverage }) => {
  const { darkMode } = useDarkMode();
  
  const bgColor = darkMode ? 'bg-gray-800' : 'bg-white';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  
  // Calculate category-level statistics
  const categoryStats = useMemo(() => {
    const stats: Record<string, {
      total: number;
      strong: number;
      adequate: number;
      weak: number;
      critical: number;
      totalEvidence: number;
      avgCoverage: number;
    }> = {};
    
    coverage.forEach(item => {
      if (!stats[item.category]) {
        stats[item.category] = {
          total: 0,
          strong: 0,
          adequate: 0,
          weak: 0,
          critical: 0,
          totalEvidence: 0,
          avgCoverage: 0
        };
      }
      
      const stat = stats[item.category];
      stat.total++;
      stat[item.status]++;
      stat.totalEvidence += item.evidenceCount;
      stat.avgCoverage += item.coveragePercent;
    });
    
    // Calculate averages
    Object.values(stats).forEach(stat => {
      stat.avgCoverage = Math.round(stat.avgCoverage / stat.total);
    });
    
    return stats;
  }, [coverage]);
  
  const getCategoryStatus = (stat: typeof categoryStats[string]) => {
    if (stat.critical > 0) return 'critical';
    if (stat.weak > stat.adequate) return 'weak';
    if (stat.strong >= stat.total / 2) return 'strong';
    return 'adequate';
  };
  
  const getCategoryIcon = (status: string) => {
    switch (status) {
      case 'strong':
        return <CheckCircleIcon className="w-6 h-6 text-green-500" />;
      case 'adequate':
        return <CheckCircleIcon className="w-6 h-6 text-blue-500" />;
      case 'weak':
        return <ExclamationTriangleIcon className="w-6 h-6 text-yellow-500" />;
      case 'critical':
        return <XCircleIcon className="w-6 h-6 text-red-500" />;
      default:
        return null;
    }
  };
  
  const getCategoryColor = (status: string) => {
    switch (status) {
      case 'strong':
        return 'text-green-600 dark:text-green-400';
      case 'adequate':
        return 'text-blue-600 dark:text-blue-400';
      case 'weak':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'critical':
        return 'text-red-600 dark:text-red-400';
      default:
        return mutedTextColor;
    }
  };
  
  return (
    <div className={`${bgColor} rounded-lg border ${borderColor} p-6`}>
      <h3 className={`text-lg font-semibold mb-6 ${textColor}`}>
        Evidence Health Check
      </h3>
      
      <div className="space-y-4">
        {Object.entries(categoryStats).map(([category, stat], index) => {
          const status = getCategoryStatus(stat);
          
          return (
            <motion.div
              key={category}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'} rounded-lg p-4`}
            >
              {/* Category Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  {getCategoryIcon(status)}
                  <div>
                    <h4 className={`font-medium ${textColor}`}>{category}</h4>
                    <p className={`text-sm ${mutedTextColor}`}>
                      {stat.total} criteria • {stat.totalEvidence} evidence cards
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-2xl font-bold ${getCategoryColor(status)}`}>
                    {stat.avgCoverage}%
                  </div>
                  <p className={`text-xs ${mutedTextColor}`}>avg coverage</p>
                </div>
              </div>
              
              {/* Coverage Breakdown */}
              <div className="grid grid-cols-4 gap-2 mb-3">
                {stat.strong > 0 && (
                  <div className="text-center">
                    <div className="text-lg font-semibold text-green-600 dark:text-green-400">
                      {stat.strong}
                    </div>
                    <div className={`text-xs ${mutedTextColor}`}>Strong</div>
                  </div>
                )}
                {stat.adequate > 0 && (
                  <div className="text-center">
                    <div className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                      {stat.adequate}
                    </div>
                    <div className={`text-xs ${mutedTextColor}`}>Good</div>
                  </div>
                )}
                {stat.weak > 0 && (
                  <div className="text-center">
                    <div className="text-lg font-semibold text-yellow-600 dark:text-yellow-400">
                      {stat.weak}
                    </div>
                    <div className={`text-xs ${mutedTextColor}`}>Weak</div>
                  </div>
                )}
                {stat.critical > 0 && (
                  <div className="text-center">
                    <div className="text-lg font-semibold text-red-600 dark:text-red-400">
                      {stat.critical}
                    </div>
                    <div className={`text-xs ${mutedTextColor}`}>Critical</div>
                  </div>
                )}
              </div>
              
              {/* Progress Bar */}
              <div className="relative">
                <div className={`h-2 rounded-full ${darkMode ? 'bg-gray-600' : 'bg-gray-200'} overflow-hidden`}>
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${stat.avgCoverage}%` }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className={`h-full ${
                      status === 'strong' ? 'bg-green-500' :
                      status === 'adequate' ? 'bg-blue-500' :
                      status === 'weak' ? 'bg-yellow-500' :
                      'bg-red-500'
                    }`}
                  />
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
      
      {/* Summary Message */}
      <div className={`mt-6 text-sm ${mutedTextColor} flex items-center gap-2`}>
        <ChartBarIcon className="w-4 h-4" />
        <span>
          Add evidence in the My Goals or Company Rubric tabs to improve coverage
        </span>
      </div>
    </div>
  );
};

export default EvidenceHealthCheck;